import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { DashboardNavbar } from '@/components/dashboard/DashboardNavbar';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { createDraftFromLead } from '@/services/draftService';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import type { CreateDraftInput } from '@/types/draft';

const CATEGORIES = [
  'Barber',
  'Stylist',
  'Nail Tech',
  'Lash Tech',
  'Trainer',
  'Tattoo',
  'Esthetician',
  'Massage Therapist',
  'Braider',
  'Other',
];

export default function DraftNewPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState<CreateDraftInput>({
    instagram_handle: '',
    business_name: '',
    provider_name: '',
    category: '',
    city: '',
    booking_url: '',
    website_url: '',
    phone: '',
    notes: '',
  });

  if (profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const set = (field: keyof CreateDraftInput) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.business_name && !form.provider_name && !form.instagram_handle) {
      toast.error('Enter at least a business name, provider name, or Instagram handle.');
      return;
    }

    setSubmitting(true);
    try {
      const draft = await createDraftFromLead(form);
      toast.success('Draft created');
      navigate(`/admin/drafts/${draft.id}`);
    } catch (err: any) {
      console.error('Failed to create draft:', err);
      toast.error(err.message || 'Failed to create draft');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-figgy-600" />
              <h1 className="text-2xl font-semibold">New Draft</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Create a premium Figgy preview page for a prospective lead.
            </p>
          </div>

          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Identity */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Identity
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Instagram Handle</label>
                    <Input
                      placeholder="@handle"
                      value={form.instagram_handle}
                      onChange={set('instagram_handle')}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Business Name</label>
                    <Input
                      placeholder="Studio Luxe"
                      value={form.business_name}
                      onChange={set('business_name')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Provider Name</label>
                    <Input
                      placeholder="Marcus Cole"
                      value={form.provider_name}
                      onChange={set('provider_name')}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Category</label>
                    <Select
                      value={form.category}
                      onValueChange={(val) =>
                        setForm((prev) => ({ ...prev, category: val }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c.toLowerCase()}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">City</label>
                  <Input
                    placeholder="Los Angeles"
                    value={form.city}
                    onChange={set('city')}
                  />
                </div>
              </div>

              {/* Links */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Links
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Existing Booking Link</label>
                    <Input
                      placeholder="https://booksy.com/..."
                      value={form.booking_url}
                      onChange={set('booking_url')}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Website</label>
                    <Input
                      placeholder="https://..."
                      value={form.website_url}
                      onChange={set('website_url')}
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Contact
                </h3>
                <div>
                  <label className="text-sm font-medium mb-1 block">Phone</label>
                  <Input
                    placeholder="555-123-4567"
                    value={form.phone}
                    onChange={set('phone')}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Internal Notes
                </h3>
                <Textarea
                  placeholder="Notes about this lead (internal only, never shown publicly)..."
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Draft'
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
